import { useState } from 'react';
import { createPortal } from 'react-dom';
import { DndContext, DragOverlay, PointerSensor, KeyboardSensor, useSensor, useSensors, useDroppable, closestCenter, pointerWithin } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { MenuOrderRow } from '@/components/MenuOrderRow';
import { moveMenuItem } from '@/lib/menuOrder';

const GroupTarget = ({ section, disabled, empty }) => {
  const { setNodeRef, isOver } = useDroppable({ id: `group:${section}`, disabled });
  return <div ref={setNodeRef} className={`mx-2 mt-2 px-3 py-2 text-[11px] font-bold uppercase border transition-colors ${isOver ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-transparent text-gray-500'} ${empty ? 'border-dashed border-gray-200 min-h-12' : ''}`}
    data-testid={`menu-drop-group-${section.toLowerCase()}`}>{section}</div>;
};

export const MenuOrderEditor = ({ groups, visible, disabled, onChange }) => {
  const [active, setActive] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const shown = groups.map(g => ({ ...g, items: g.items.filter(visible) }));
  const flat = shown.flatMap(g => g.items);
  const activeItem = flat.find(i => i.to === active);
  const move = (route, section, target, after) => onChange(moveMenuItem(groups, route, section, target, after));
  const step = (route, direction) => {
    const other = flat[flat.findIndex(i => i.to === route) + direction];
    const group = shown.find(g => g.items.some(i => i.to === other?.to));
    if (group) move(route, group.section, other.to, direction > 0);
  };
  const drop = ({ active: source, over }) => {
    setActive(null);
    if (!over || source.id === over.id || disabled) return;
    if (String(over.id).startsWith('group:')) { move(source.id, String(over.id).slice(6)); return; }
    const target = groups.find(g => g.items.some(i => i.to === over.id));
    if (!target) return;
    const oldIndex = target.items.findIndex(i => i.to === source.id);
    const targetIndex = target.items.findIndex(i => i.to === over.id);
    move(source.id, target.section, over.id, oldIndex >= 0 && oldIndex < targetIndex);
  };
  return <DndContext sensors={sensors} collisionDetection={args => pointerWithin(args).length ? pointerWithin(args) : closestCenter(args)}
    onDragStart={({ active: source }) => setActive(source.id)} onDragCancel={() => setActive(null)} onDragEnd={drop}
    accessibility={{ screenReaderInstructions: { draggable: 'Druk op spatie om op te pakken, gebruik de pijltjestoetsen en druk op spatie om neer te zetten. Escape annuleert.' } }}>
    <SortableContext items={flat.map(i => i.to)}>
      {shown.map(g => <div key={g.section} data-testid={`menu-order-group-${g.section.toLowerCase()}`}>
        <GroupTarget section={g.section} disabled={disabled} empty={!g.items.length} />
        {g.items.map(item => <MenuOrderRow key={item.to} item={item} section={g.section} groups={shown} disabled={disabled}
          first={flat[0]?.to === item.to} last={flat[flat.length - 1]?.to === item.to}
          onStep={direction => step(item.to, direction)} onMove={section => move(item.to, section)} />)}
      </div>)}
    </SortableContext>
    {createPortal(<DragOverlay dropAnimation={null}>{activeItem && <div className="bg-white border border-blue-300 shadow-lg px-3 py-2 text-xs font-semibold max-w-56" data-testid="menu-drag-preview">{activeItem.label}</div>}</DragOverlay>, document.body)}
  </DndContext>;
};